// Pure logic for the intelligence report cover page. Kept separate from CoverPage.tsx
// so it can be unit-tested without pulling in @react-pdf/renderer.

import type { IntelligenceReportData, KeyIndicator } from "./types"

export const COVER_GREEN = "#22C55E"
export const COVER_YELLOW = "#F59E0B"
export const COVER_RED = "#EF4444"

export type PriorityBucket = "high" | "mid" | "low"

export const getPriorityBucket = (p: number): PriorityBucket => {
  if (p >= 0.7) return "high"
  if (p >= 0.2) return "mid"
  return "low"
}

export type PriorityCopy = {
  greetingSubtitle: string
  headline: string
  statusLabel: string
  statusDescription: string
  color: string
  gradient: [string, string]
}

export const PRIORITY_COVER_COPY: Record<PriorityBucket, PriorityCopy> = {
  high: {
    greetingSubtitle: "Your system needs some help",
    headline: "Your HVAC Needs Some Help",
    statusLabel: "Service Needed",
    statusDescription: "We recommend scheduling a visit soon.",
    color: COVER_RED,
    gradient: ["#FF6376", "#CB2382"],
  },
  mid: {
    greetingSubtitle: "Here's your home comfort update",
    headline: "A Few Tweaks Could Save You Money",
    statusLabel: "Some Attention\nNeeded",
    statusDescription: "A couple of changes can improve comfort and lower your energy bill.",
    color: COVER_YELLOW,
    gradient: ["#F07F79", "#F5A623"],
  },
  low: {
    greetingSubtitle: "Great news about your home",
    headline: "Everything Looks Good",
    statusLabel: "All Good",
    statusDescription: "Your system is running well. No action needed at this time.",
    color: COVER_GREEN,
    gradient: ["#22C55E", "#15803D"],
  },
}

export type CoverTile = { label: string; value: string; color: string; description: string }

export const getAirQualityTile = (ind?: KeyIndicator): CoverTile | null => {
  if (!ind) return null
  const v = parseFloat(ind.value_raw ?? "")
  if (Number.isNaN(v)) return null
  const base = { label: "AIR QUALITY" }
  if (v <= 2.0)
    return { ...base, value: "Excellent", color: COVER_GREEN, description: "Clean, healthy air in your home." }
  if (v <= 3.0) return { ...base, value: "Good", color: COVER_GREEN, description: "The air quality is good!" }
  if (v <= 4.0)
    return { ...base, value: "Fair", color: COVER_YELLOW, description: "Periodically ventilating is recommended." }
  if (v <= 5.0)
    return {
      ...base,
      value: "Bad",
      color: COVER_RED,
      description: "Filter replacement or airflow inspection may be needed.",
    }
  return {
    ...base,
    value: "Very Bad",
    color: COVER_RED,
    description:
      "The air quality during the period was extremely bad, consider replacing your filter or improving ventilation.",
  }
}

export const getComfortTile = (ind?: KeyIndicator): CoverTile | null => {
  if (!ind) return null
  const raw = ind.value_raw ?? ""
  const v = parseFloat(raw)
  if (Number.isNaN(v)) return null
  const base = { label: "COMFORT" }
  if (v <= -3.1)
    return { ...base, value: "Too Cold", color: COVER_RED, description: `${raw} below your target temperature.` }
  if (v <= -1.1)
    return {
      ...base,
      value: "De-Elevated",
      color: COVER_YELLOW,
      description: `The indoor temperature was periodically lower your set point by ${raw}.`,
    }
  if (v <= 1.0)
    return { ...base, value: "On Target", color: COVER_GREEN, description: "Perfectly hitting your set temperature." }
  if (v <= 3.0)
    return {
      ...base,
      value: "Elevated",
      color: COVER_YELLOW,
      description: `The indoor temperature was periodically higher your set point by ${raw}.`,
    }
  return { ...base, value: "Too Warm", color: COVER_RED, description: `${raw} above your target temperature.` }
}

export const getHumidityTile = (ind?: KeyIndicator): CoverTile | null => {
  if (!ind) return null
  const v = parseFloat(ind.value_raw ?? "")
  if (Number.isNaN(v)) return null
  const base = { label: "HUMIDITY" }
  if (v < 40)
    return {
      ...base,
      value: "Too Dry",
      color: COVER_RED,
      description: "Adding a humidifier can improve comfort and reduce dryness.",
    }
  if (v < 45) return { ...base, value: "De-Elevated", color: COVER_YELLOW, description: "A little lower than typical." }
  if (v <= 55)
    return {
      ...base,
      value: "Just Right",
      color: COVER_GREEN,
      description: "Moisture levels are healthy and comfortable.",
    }
  if (v <= 60) return { ...base, value: "Elevated", color: COVER_YELLOW, description: "A little higher than typical." }
  return {
    ...base,
    value: "Too Humid",
    color: COVER_RED,
    description: "Sticky air \u2014 may cause moisture issues.",
  }
}

export const getRuntimeTile = (ind?: KeyIndicator): CoverTile | null => {
  if (!ind) return null
  const raw = ind.value_raw ?? ""
  const pct = parseFloat(raw)
  if (Number.isNaN(pct)) return null
  const base = { label: "HVAC RUNTIME" }
  if (pct < 30)
    return {
      ...base,
      value: "Excellent",
      color: COVER_GREEN,
      description: `The system ran for ${raw} of the reporting period, which is within the expected range.`,
    }
  if (pct <= 70)
    return {
      ...base,
      value: "Good",
      color: COVER_GREEN,
      description: `The system ran for ${raw} of the reporting period \u2014 a little higher than normal.`,
    }
  if (pct < 95)
    return {
      ...base,
      value: "Elevated",
      color: COVER_YELLOW,
      description: `The system ran for ${raw} of the reporting period \u2014 higher than normal.`,
    }
  return {
    ...base,
    value: "Nonstop",
    color: COVER_RED,
    description: `The system ran nearly nonstop \u2014 about ${raw} of the reporting period.`,
  }
}

export const getCoverTiles = (keyIndicators: KeyIndicator[]): CoverTile[] => {
  const find = (metric: string) => keyIndicators.find(ind => ind.metric === metric)
  return [
    getRuntimeTile(find("median_total_runtime") || find("median_cooling_runtime")),
    getComfortTile(find("median_setpoint_error")),
    getHumidityTile(find("median_indoor_dew_point")),
    getAirQualityTile(find("median_iaq_index")),
  ].filter((t): t is CoverTile => t !== null)
}

export const getFirstSummaryBullet = (
  detailSummaries: Array<{ domain_code: string; assessment: string }>,
): string | undefined => {
  const order = ["overall", "sizing_runtime", "setpoints", "humidity", "indoor_air_quality"]
  for (const domain of order) {
    const item = detailSummaries.find(s => s.domain_code === domain)
    if (item?.assessment) return item.assessment
  }
  return undefined
}

export const buildGreetingName = (customerName?: string, customerEmail?: string): string =>
  [customerName, customerEmail ? `(${customerEmail})` : null].filter(Boolean).join(" ").trim()

export type CoverData = {
  priorityPct: number
  copy: PriorityCopy
  tagline: string
  greetingName: string
  tiles: CoverTile[]
}

export const getCoverData = (report: IntelligenceReportData): CoverData => {
  const priorityValue = Math.min(Math.max(report.priority_num ?? 0, 0), 1)
  const priorityPct = Math.round(priorityValue * 100)
  const copy = PRIORITY_COVER_COPY[getPriorityBucket(priorityValue)]
  const tagline = getFirstSummaryBullet(report.detail_summaries) ?? copy.statusDescription
  const greetingName = buildGreetingName(report.customer_name, report.customer_email)
  const tiles = getCoverTiles(report.key_indicators)
  return { priorityPct, copy, tagline, greetingName, tiles }
}

export const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace("#", "")
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

export const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map(v => Math.round(v).toString(16).padStart(2, "0")).join("")}`

export const lerpColor = (from: string, to: string, t: number): string => {
  const [r1, g1, b1] = hexToRgb(from)
  const [r2, g2, b2] = hexToRgb(to)
  return rgbToHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t)
}
